import { useEffect } from "react";
import hljs from "highlight.js";

// const IST_OFFSET = 330;

// export const istToUtc = (date) => {
//     if(!date) return null;
//     const d = new Date(date);
//     return new Date(d.getTime() - IST_OFFSET * 60 * 1000);
// };

// True if a RichTextEditor HTML value has no visible content (ignores empty
// tags like "<p></p>"), but treats an embedded image as non-empty.
export const isHtmlEmpty = (html) => {
  if (!html) return true;
  if (/<img\s/i.test(html)) return false;
  return html.replace(/<[^>]*>/g, "").trim().length === 0;
};

// Re-runs highlight.js over any code blocks rendered inside `ref` (e.g. via
// dangerouslySetInnerHTML) whenever `deps` changes. Needed because saved
// RichTextEditor HTML only carries plain `<pre><code class="language-xxx">`
// markup — the colored hljs-* spans are a live-editor-only decoration and
// are never part of the saved HTML.
export const useHighlightCode = (ref, deps = []) => {
  useEffect(() => {
    if (!ref?.current) return;
    ref.current.querySelectorAll("pre code:not([data-highlighted])").forEach((el) => {
      try { hljs.highlightElement(el); } catch (_) {}
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
};

// ─── Date/time across the UTC boundary ────────────────────────────────────────
//
// Dates are stored in Mongo as real UTC instants. A <input type="datetime-local">
// deals in the VIEWER'S local wall clock and carries no zone at all, so both
// directions have to be converted explicitly.
//
// The previous helper only did half of it: it converted UTC -> IST for display,
// but the raw input string was posted back untouched. The server (Lambda, UTC)
// then parsed that IST wall clock as if it were UTC, so every save pushed the
// test 5 hours 30 minutes later — and it compounded on each edit.
//
// These use the browser's own zone rather than a hardcoded +5:30, so the panel
// is correct for an admin in any timezone, not just IST.

const pad = (n) => String(n).padStart(2, "0");

// UTC instant -> "YYYY-MM-DDTHH:mm" in the viewer's local zone, for filling a
// datetime-local input. Uses local getters deliberately: toISOString() would
// render the UTC wall clock and reintroduce the offset bug.
export const toDateTimeLocal = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "";
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
         `T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

// datetime-local value -> ISO UTC string for the API. `new Date("...T02:41")`
// parses a zone-less datetime as LOCAL time, which is exactly what the input
// means, so this yields the correct instant.
export const fromDateTimeLocal = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "";
  return d.toISOString();
};

// Friendly display: "09 Sep 2026, 2:41 AM" in the viewer's own zone.
export const formatDateTime = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleString(undefined, {
    day: "2-digit", month: "short", year: "numeric",
    hour: "numeric", minute: "2-digit", hour12: true,
  });
};
